'use client';

import { c as _c } from "react/compiler-runtime";
import { isRiskProfileExpired } from '@repo/sdk/date';
import { Link } from '@repo/features/adapters/next';
import { Badge, Tooltip, TooltipContent, TooltipTrigger } from '@repo/ui/components';
import { DateFormatter } from '@repo/ui/date-formatter';
import { Building, Caravan, Factory, Info, type LucideIcon, Shield, ShieldAlert, ShieldCheck, ShieldMinus, ShieldQuestion, ShieldX } from '@repo/ui/icons';
import { cn } from '@repo/ui/lib/utils';
import { Fragment, type ReactElement, type ReactNode, useState } from 'react';
import { BrokerIcon } from './broker-icon';
import { CompanyCountryFlag } from './company-country-flag';
import { MarketplaceIcon } from './marketplace-icon';
import { OrganisationLogo } from './organisation-logo';
export type CompanyDetailsProps = {
  className?: string;
  company: CompanyResult;
  /** Where each count opens; a count without a link shows as plain text. */
  links?: Partial<Record<keyof CompanyStatistics, string>>;
  /**
   * Shows an infotip on how often the counts refresh. `null` means they have not been
   * calculated yet; leave it out where the source has no refresh time, as in company search.
   */
  statisticsUpdatedAt?: null | string;
};
export type CompanyIconsProps = {
  company: CompanyResult;
  /** Sizes each icon, so the picker trigger can show them smaller than the result rows. */
  iconClassName?: string;
  /**
   * Company pages lead with the company type. Search rows leave this off, so only a broker
   * shows a type icon, and it sits after the risk score.
   */
  showTypeIcon?: boolean;
};
export type CompanyResult = {
  addressLines?: readonly string[];
  country?: null | {
    code?: null | string;
    name?: null | string;
  };
  isBroker?: boolean;
  isMarketplace?: boolean;
  isXeroSynced?: boolean;
  organisationSlug?: null | string;
  riskProfile?: null | {
    score?: null | string;
    validTo?: null | string;
  };
  statistics?: CompanyStatistics;
  title: string;
  typeAlias?: 'broker' | 'haulier' | 'other' | 'shipper' | null;
  vat?: null | string;
};
export type CompanyResultRowProps = {
  className?: string;
  company: CompanyResult;
  /** Wraps a long company title onto more lines instead of truncating it. */
  wrapTitle?: boolean;
};

/** How often the company appears on loads, from the company search index. */
export type CompanyStatistics = {
  contacts: number;
  jobs: number;
  loads: number;
  locations: number;
};
type IconTooltipProps = {
  children: ReactElement;
  label: ReactNode;
};
type OverflowingCompanyTextProps = {
  children: string;
  className?: string;
};
type RiskScoreConfig = {
  className: string;
  icon: LucideIcon;
  label: string;
};
const IconTooltip = t0 => {
  const $ = _c(8);
  const {
    children,
    label
  } = t0;
  let t1;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = <span className="inline-flex shrink-0" />;
    $[0] = t1;
  } else {
    t1 = $[0];
  }
  let t2;
  if ($[1] !== children) {
    t2 = <TooltipTrigger render={t1} data-sentry-element="TooltipTrigger" data-sentry-source-file="company-result-row.tsx">{children}</TooltipTrigger>;
    $[1] = children;
    $[2] = t2;
  } else {
    t2 = $[2];
  }
  let t3;
  if ($[3] !== label) {
    t3 = <TooltipContent data-sentry-element="TooltipContent" data-sentry-source-file="company-result-row.tsx">{label}</TooltipContent>;
    $[3] = label;
    $[4] = t3;
  } else {
    t3 = $[4];
  }
  let t4;
  if ($[5] !== t2 || $[6] !== t3) {
    t4 = <Tooltip data-sentry-element="Tooltip" data-sentry-component="IconTooltip" data-sentry-source-file="company-result-row.tsx">{t2}{t3}</Tooltip>;
    $[5] = t2;
    $[6] = t3;
    $[7] = t4;
  } else {
    t4 = $[7];
  }
  return t4;
};
export const OverflowingCompanyText = t0 => {
  const $ = _c(15);
  const {
    children,
    className
  } = t0;
  const [open, setOpen] = useState(false);
  let t1;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = nextOpen => {
      if (!nextOpen) {
        setOpen(false);
      }
    };
    $[0] = t1;
  } else {
    t1 = $[0];
  }
  let t2;
  if ($[1] !== className) {
    t2 = cn("company-overflow-text block min-w-0 truncate", className);
    $[1] = className;
    $[2] = t2;
  } else {
    t2 = $[2];
  }
  let t3;
  if ($[3] === Symbol.for("react.memo_cache_sentinel")) {
    t3 = event => {
      setOpen(event.currentTarget.scrollWidth > event.currentTarget.clientWidth);
    };
    $[3] = t3;
  } else {
    t3 = $[3];
  }
  let t4;
  if ($[4] !== t2) {
    t4 = <span className={t2} onPointerEnter={t3} />;
    $[4] = t2;
    $[5] = t4;
  } else {
    t4 = $[5];
  }
  let t5;
  if ($[6] !== children || $[7] !== t4) {
    t5 = <TooltipTrigger render={t4} data-sentry-element="TooltipTrigger" data-sentry-source-file="company-result-row.tsx">{children}</TooltipTrigger>;
    $[6] = children;
    $[7] = t4;
    $[8] = t5;
  } else {
    t5 = $[8];
  }
  let t6;
  if ($[9] !== children) {
    t6 = <TooltipContent className="max-w-xs wrap-break-word whitespace-pre-wrap" data-sentry-element="TooltipContent" data-sentry-source-file="company-result-row.tsx">{children}</TooltipContent>;
    $[9] = children;
    $[10] = t6;
  } else {
    t6 = $[10];
  }
  let t7;
  if ($[11] !== open || $[12] !== t5 || $[13] !== t6) {
    t7 = <Tooltip onOpenChange={t1} open={open} data-sentry-element="Tooltip" data-sentry-component="OverflowingCompanyText" data-sentry-source-file="company-result-row.tsx">{t5}{t6}</Tooltip>;
    $[11] = open;
    $[12] = t5;
    $[13] = t6;
    $[14] = t7;
  } else {
    t7 = $[14];
  }
  return t7;
};
export const RISK_SCORE_MAP = {
  HIGH: {
    className: 'text-orange-600',
    icon: ShieldAlert,
    label: 'High risk'
  },
  LOW: {
    className: 'text-blue-500',
    icon: Shield,
    label: 'Low risk'
  },
  MEDIUM: {
    className: 'text-muted-foreground',
    icon: ShieldMinus,
    label: 'Medium risk'
  },
  'VERY HIGH': {
    className: 'text-destructive',
    icon: ShieldX,
    label: 'Very high risk'
  },
  'VERY LOW': {
    className: 'text-blue-500',
    icon: ShieldCheck,
    label: 'Very low risk'
  }
} satisfies Record<string, RiskScoreConfig>;
export type RiskScoreKey = keyof typeof RISK_SCORE_MAP;
export const isRiskScoreKey = (score: string): score is RiskScoreKey => Object.hasOwn(RISK_SCORE_MAP, score);

/** `description` names the risk and its validity date, for the icon's tooltip. */
const getRiskScoreConfig = (riskProfile: CompanyResult['riskProfile']) => {
  if (riskProfile === null) {
    return {
      className: 'text-destructive',
      description: 'Not verified',
      icon: ShieldQuestion,
      label: 'Not verified'
    };
  }
  const score = riskProfile?.score?.trim().toUpperCase();
  if (!score) return null;
  if (!isRiskScoreKey(score)) return null;
  const config = RISK_SCORE_MAP[score];
  if (!riskProfile?.validTo) return {
    ...config,
    description: config.label
  };
  const validTo = <DateFormatter date={riskProfile.validTo} kind="date" />;
  if (isRiskProfileExpired(riskProfile.validTo)) {
    return {
      className: 'text-destructive',
      description: <>
          {config.label}, expired {validTo}
        </>,
      icon: ShieldX,
      label: 'Risk score expired'
    };
  }
  return {
    ...config,
    description: <>
        {config.label}, valid until {validTo}
      </>
  };
};
const pluralize = (count: number, noun: string) => `${count} ${noun}${count === 1 ? '' : 's'}`;
const STATISTIC_NOUNS = {
  contacts: 'contact',
  jobs: 'job',
  loads: 'load',
  locations: 'location'
} satisfies Record<keyof CompanyStatistics, string>;
const STATISTIC_ORDER = ['loads', 'jobs', 'locations', 'contacts'] as const;
const STATISTIC_LINK_TOOLTIPS = {
  contacts: 'Go to contacts',
  jobs: 'Go to jobs',
  loads: 'Go to loads',
  locations: 'Go to locations'
} satisfies Record<keyof CompanyStatistics, string>;

/** Counts that are zero are left out, so a company without activity has no business line. */
const toStatisticItems = (statistics: CompanyStatistics) => STATISTIC_ORDER.filter(key => statistics[key] > 0).map(key => ({
  key,
  label: pluralize(statistics[key], STATISTIC_NOUNS[key])
}));
const COMPANY_TYPE_ICONS = {
  haulier: {
    icon: Caravan,
    label: 'Carrier'
  },
  other: {
    icon: Building,
    label: 'Other'
  },
  shipper: {
    icon: Factory,
    label: 'Customer'
  }
} as const;
const CompanyTypeIcon = t0 => {
  const $ = _c(11);
  const {
    company,
    iconClassName
  } = t0;
  const typeAlias = company.typeAlias ?? (company.isBroker ? "broker" : "other");
  if (typeAlias === "broker") {
    let t1;
    if ($[0] !== iconClassName) {
      t1 = <IconTooltip label="Broker: can act as both customer and carrier."><BrokerIcon className={iconClassName} /></IconTooltip>;
      $[0] = iconClassName;
      $[1] = t1;
    } else {
      t1 = $[1];
    }
    return t1;
  }
  const {
    icon: Icon,
    label
  } = COMPANY_TYPE_ICONS[typeAlias];
  let t1;
  if ($[2] !== iconClassName) {
    t1 = cn("text-muted-foreground", iconClassName);
    $[2] = iconClassName;
    $[3] = t1;
  } else {
    t1 = $[3];
  }
  let t2;
  if ($[4] !== Icon || $[5] !== label || $[6] !== t1) {
    t2 = <Icon aria-label={label} className={t1} role="img" data-sentry-element="Icon" data-sentry-source-file="company-result-row.tsx" />;
    $[4] = Icon;
    $[5] = label;
    $[6] = t1;
    $[7] = t2;
  } else {
    t2 = $[7];
  }
  let t3;
  if ($[8] !== label || $[9] !== t2) {
    t3 = <IconTooltip label={label} data-sentry-element="IconTooltip" data-sentry-component="CompanyTypeIcon" data-sentry-source-file="company-result-row.tsx">{t2}</IconTooltip>;
    $[8] = label;
    $[9] = t2;
    $[10] = t3;
  } else {
    t3 = $[10];
  }
  return t3;
};

/** A 3PL shows only its own icon: operations staff know every 3PL by name. */
export const CompanyIcons = t0 => {
  const $ = _c(29);
  const {
    company,
    iconClassName: t1,
    showTypeIcon: t2
  } = t0;
  const iconClassName = t1 === undefined ? "size-5" : t1;
  const showTypeIcon = t2 === undefined ? false : t2;
  let t3;
  if ($[0] !== company || $[1] !== iconClassName || $[2] !== showTypeIcon) {
    t3 = showTypeIcon ? <CompanyTypeIcon company={company} iconClassName={iconClassName} /> : null;
    $[0] = company;
    $[1] = iconClassName;
    $[2] = showTypeIcon;
    $[3] = t3;
  } else {
    t3 = $[3];
  }
  const typeIcon = t3;
  if (company.isMarketplace) {
    let t4;
    if ($[4] !== iconClassName) {
      t4 = <IconTooltip label="3PL intermediary with the option to define the end company."><MarketplaceIcon className={iconClassName} /></IconTooltip>;
      $[4] = iconClassName;
      $[5] = t4;
    } else {
      t4 = $[5];
    }
    let t5;
    if ($[6] !== t4 || $[7] !== typeIcon) {
      t5 = <>{typeIcon}{t4}</>;
      $[6] = t4;
      $[7] = typeIcon;
      $[8] = t5;
    } else {
      t5 = $[8];
    }
    return t5;
  }
  let t4;
  let t5;
  if ($[9] !== company.riskProfile || $[10] !== iconClassName || $[11] !== typeIcon) {
    const riskScore = getRiskScoreConfig(company.riskProfile);
    const RiskIcon = riskScore?.icon;
    t4 = typeIcon;
    t5 = RiskIcon ? <IconTooltip label={riskScore.description}><RiskIcon aria-label={riskScore.label} className={cn(iconClassName, riskScore.className)} role="img" /></IconTooltip> : null;
    $[9] = company.riskProfile;
    $[10] = iconClassName;
    $[11] = typeIcon;
    $[12] = t4;
    $[13] = t5;
  } else {
    t4 = $[12];
    t5 = $[13];
  }
  let t6;
  if ($[14] !== company.isBroker || $[15] !== iconClassName || $[16] !== showTypeIcon) {
    t6 = company.isBroker && !showTypeIcon ? <IconTooltip label="Broker: can act as both customer and carrier."><BrokerIcon className={iconClassName} /></IconTooltip> : null;
    $[14] = company.isBroker;
    $[15] = iconClassName;
    $[16] = showTypeIcon;
    $[17] = t6;
  } else {
    t6 = $[17];
  }
  let t7;
  if ($[18] !== company.country || $[19] !== iconClassName) {
    t7 = company.country?.code ? <IconTooltip label={company.country.name ?? company.country.code}><CompanyCountryFlag className={iconClassName} code={company.country.code} name={company.country.name} /></IconTooltip> : null;
    $[18] = company.country;
    $[19] = iconClassName;
    $[20] = t7;
  } else {
    t7 = $[20];
  }
  let t8;
  if ($[21] !== company.isXeroSynced) {
    t8 = company.isXeroSynced ? <IconTooltip label="Synced with Xero"><Badge variant="outline">Xero</Badge></IconTooltip> : null;
    $[21] = company.isXeroSynced;
    $[22] = t8;
  } else {
    t8 = $[22];
  }
  let t9;
  if ($[23] !== t4 || $[24] !== t5 || $[25] !== t6 || $[26] !== t7 || $[27] !== t8) {
    t9 = <>{t4}{t5}{t6}{t7}{t8}</>;
    $[23] = t4;
    $[24] = t5;
    $[25] = t6;
    $[26] = t7;
    $[27] = t8;
    $[28] = t9;
  } else {
    t9 = $[28];
  }
  return t9;
};
export const CompanyResultRow = t0 => {
  const $ = _c(23);
  const {
    className,
    company,
    wrapTitle: t1
  } = t0;
  const wrapTitle = t1 === undefined ? false : t1;
  let t2;
  if ($[0] !== className) {
    t2 = cn("flex min-w-0 flex-1 flex-col gap-1 text-left", className);
    $[0] = className;
    $[1] = t2;
  } else {
    t2 = $[1];
  }
  const t3 = wrapTitle && "break-words whitespace-normal";
  let t4;
  if ($[2] !== t3) {
    t4 = cn("text-base leading-6 font-semibold", t3);
    $[2] = t3;
    $[3] = t4;
  } else {
    t4 = $[3];
  }
  let t5;
  if ($[4] !== company.title || $[5] !== t4) {
    t5 = <OverflowingCompanyText className={t4} data-sentry-element="OverflowingCompanyText" data-sentry-source-file="company-result-row.tsx">{company.title}</OverflowingCompanyText>;
    $[4] = company.title;
    $[5] = t4;
    $[6] = t5;
  } else {
    t5 = $[6];
  }
  let t6;
  if ($[7] !== company) {
    t6 = <CompanyIcons company={company} data-sentry-element="CompanyIcons" data-sentry-source-file="company-result-row.tsx" />;
    $[7] = company;
    $[8] = t6;
  } else {
    t6 = $[8];
  }
  let t7;
  if ($[9] !== t5 || $[10] !== t6) {
    t7 = <div className="flex min-w-0 items-center gap-2">{t5}{t6}</div>;
    $[9] = t5;
    $[10] = t6;
    $[11] = t7;
  } else {
    t7 = $[11];
  }
  let t8;
  if ($[12] !== company.organisationSlug) {
    t8 = company.organisationSlug ? <OrganisationLogo className="text-muted-foreground/60" slug={company.organisationSlug} /> : null;
    $[12] = company.organisationSlug;
    $[13] = t8;
  } else {
    t8 = $[13];
  }
  let t9;
  if ($[14] !== t7 || $[15] !== t8) {
    t9 = <div className="flex min-w-0 items-start justify-between gap-2">{t7}{t8}</div>;
    $[14] = t7;
    $[15] = t8;
    $[16] = t9;
  } else {
    t9 = $[16];
  }
  let t10;
  if ($[17] !== company) {
    t10 = <CompanyDetails company={company} data-sentry-element="CompanyDetails" data-sentry-source-file="company-result-row.tsx" />;
    $[17] = company;
    $[18] = t10;
  } else {
    t10 = $[18];
  }
  let t11;
  if ($[19] !== t10 || $[20] !== t2 || $[21] !== t9) {
    t11 = <div className={t2} data-sentry-component="CompanyResultRow" data-sentry-source-file="company-result-row.tsx">{t9}{t10}</div>;
    $[19] = t10;
    $[20] = t2;
    $[21] = t9;
    $[22] = t11;
  } else {
    t11 = $[22];
  }
  return t11;
};
const StatisticsInfotip = t0 => {
  const $ = _c(14);
  const {
    revealOnHover,
    updatedAt
  } = t0;
  const t1 = revealOnHover && "data-popup-open:opacity-100 md:opacity-0 md:group-hover/statistics:opacity-100";
  let t2;
  if ($[0] !== t1) {
    t2 = cn("ml-1 inline-flex shrink-0 align-middle transition-opacity duration-150", t1);
    $[0] = t1;
    $[1] = t2;
  } else {
    t2 = $[1];
  }
  let t3;
  if ($[2] === Symbol.for("react.memo_cache_sentinel")) {
    t3 = <span />;
    $[2] = t3;
  } else {
    t3 = $[2];
  }
  let t4;
  if ($[3] === Symbol.for("react.memo_cache_sentinel")) {
    t4 = <Info className="size-3.5" data-sentry-element="Info" data-sentry-source-file="company-result-row.tsx" />;
    $[3] = t4;
  } else {
    t4 = $[3];
  }
  let t5;
  if ($[4] !== t2) {
    t5 = <TooltipTrigger aria-label="About these counts" className={t2} render={t3} data-sentry-element="TooltipTrigger" data-sentry-source-file="company-result-row.tsx">{t4}</TooltipTrigger>;
    $[4] = t2;
    $[5] = t5;
  } else {
    t5 = $[5];
  }
  let t6;
  if ($[6] === Symbol.for("react.memo_cache_sentinel")) {
    t6 = <p>Counts update about a minute after a load, job, location or contact changes.</p>;
    $[6] = t6;
  } else {
    t6 = $[6];
  }
  let t7;
  if ($[7] !== updatedAt) {
    t7 = updatedAt ? <>Last updated <DateFormatter date={updatedAt} kind="relative" /></> : "Not calculated yet.";
    $[7] = updatedAt;
    $[8] = t7;
  } else {
    t7 = $[8];
  }
  let t8;
  if ($[9] !== t7) {
    t8 = <TooltipContent className="flex max-w-xs flex-col items-start gap-1" data-sentry-element="TooltipContent" data-sentry-source-file="company-result-row.tsx">{t6}<p>{t7}</p></TooltipContent>;
    $[9] = t7;
    $[10] = t8;
  } else {
    t8 = $[10];
  }
  let t9;
  if ($[11] !== t5 || $[12] !== t8) {
    t9 = <Tooltip data-sentry-element="Tooltip" data-sentry-component="StatisticsInfotip" data-sentry-source-file="company-result-row.tsx">{t5}{t8}</Tooltip>;
    $[11] = t5;
    $[12] = t8;
    $[13] = t9;
  } else {
    t9 = $[13];
  }
  return t9;
};

/** The activity counts, address and VAT under a company name. */
export const CompanyDetails = t0 => {
  const $ = _c(24);
  const {
    className,
    company,
    links,
    statisticsUpdatedAt
  } = t0;
  let t1;
  let t2;
  let t3;
  let t4;
  let t5;
  if ($[0] !== className || $[1] !== company.addressLines?.length || $[2] !== company.statistics || $[3] !== company.vat || $[4] !== links || $[5] !== statisticsUpdatedAt) {
    t5 = Symbol.for("react.early_return_sentinel");
    bb0: {
      const statistics = company.statistics ? toStatisticItems(company.statistics) : [];
      const showStatistics = statistics.length > 0 || statisticsUpdatedAt === null;
      if (!showStatistics && !company.addressLines?.length && !company.vat) {
        t5 = null;
        break bb0;
      }
      if ($[11] !== className) {
        t1 = cn("flex min-w-0 flex-col gap-0.5", className);
        $[11] = className;
        $[12] = t1;
      } else {
        t1 = $[12];
      }
      t2 = "CompanyDetails";
      t3 = "company-result-row.tsx";
      t4 = showStatistics ? <p className={cn("text-xs leading-4 text-muted-foreground", "group/statistics flex flex-wrap items-center gap-x-1 pb-1 font-medium")}>{links ? statistics.map((t6, index) => {
          const {
            key,
            label
          } = t6;
          const href = links[key];
          return <Fragment key={key}>{index > 0 ? <span aria-hidden={true}>•</span> : null}{href ? <Tooltip><TooltipTrigger className="hover:text-foreground" render={<Link href={href} rel="noreferrer" target="_blank" />}>{label}</TooltipTrigger><TooltipContent>{STATISTIC_LINK_TOOLTIPS[key]}</TooltipContent></Tooltip> : <span>{label}</span>}</Fragment>;
        }) : statistics.map(_temp).join(" \u2022 ")}{statisticsUpdatedAt === undefined ? null : <StatisticsInfotip revealOnHover={statistics.length > 0} updatedAt={statisticsUpdatedAt} />}</p> : null;
    }
    $[0] = className;
    $[1] = company.addressLines?.length;
    $[2] = company.statistics;
    $[3] = company.vat;
    $[4] = links;
    $[5] = statisticsUpdatedAt;
    $[6] = t1;
    $[7] = t2;
    $[8] = t3;
    $[9] = t4;
    $[10] = t5;
  } else {
    t1 = $[6];
    t2 = $[7];
    t3 = $[8];
    t4 = $[9];
    t5 = $[10];
  }
  if (t5 !== Symbol.for("react.early_return_sentinel")) {
    return t5;
  }
  let t6;
  if ($[13] !== company.addressLines) {
    t6 = company.addressLines?.length ? <div className="pb-1">{company.addressLines.map(_temp2)}</div> : null;
    $[13] = company.addressLines;
    $[14] = t6;
  } else {
    t6 = $[14];
  }
  let t7;
  if ($[15] !== company.vat) {
    t7 = company.vat ? <OverflowingCompanyText className="text-xs leading-4 text-muted-foreground">{`VAT: ${company.vat}`}</OverflowingCompanyText> : null;
    $[15] = company.vat;
    $[16] = t7;
  } else {
    t7 = $[16];
  }
  let t8;
  if ($[17] !== t1 || $[18] !== t2 || $[19] !== t3 || $[20] !== t4 || $[21] !== t6 || $[22] !== t7) {
    t8 = <div className={t1} data-sentry-component={t2} data-sentry-source-file={t3}>{t4}{t6}{t7}</div>;
    $[17] = t1;
    $[18] = t2;
    $[19] = t3;
    $[20] = t4;
    $[21] = t6;
    $[22] = t7;
    $[23] = t8;
  } else {
    t8 = $[23];
  }
  return t8;
};
function _temp(t0) {
  const {
    label: label_0
  } = t0;
  return label_0;
}
function _temp2(line) {
  return <OverflowingCompanyText className="text-xs leading-4 text-muted-foreground" key={line}>{line}</OverflowingCompanyText>;
}